import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ExpandIcon, XIcon } from 'lucide-react';
interface Stat {
  value: string;
  label: string;
}
interface StripeScreenshot {
  src: string;
  alt: string;
  caption: string;
}
const STATS: Stat[] = [{
  value: '$13.3M',
  label: 'gross volume processed'
}, {
  value: '+279%',
  label: 'gross volume, period over period'
}, {
  value: '21,306',
  label: 'successful payments (+414%)'
}, {
  value: '$210K',
  label: 'MRR (+124%)'
}];
const FEATURED_SCREENSHOT: StripeScreenshot = {
  src: 'https://cdn.magicpatterns.com/uploads/dbfJnu6ZeDMYsiGWSNeU3s/Case-Studies-13.png',
  alt: 'Stripe dashboard showing 2,034 new customers from January 2020 through January 2026.',
  caption: '2,034 new customers'
};
const CHARCOAL_TEXTURE_URL = 'https://cdn.magicpatterns.com/uploads/scC5pVG3EUePxKUfSG2TRr/Screenshot_2026-07-23_at_10.49.48_AM.png';
export function StatsSection() {
  const [activeScreenshot, setActiveScreenshot] = useState<StripeScreenshot | null>(null);
  return <section id="proof" className="w-full bg-[#111415] text-white" style={{
    backgroundImage: `radial-gradient(ellipse 62% 120% at 50% 48%, rgba(21, 47, 74, 0.9) 0%, rgba(15, 28, 41, 0.72) 46%, rgba(17, 20, 21, 0.2) 100%), url('${CHARCOAL_TEXTURE_URL}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  }} data-id="element-113" data-mp="sgvng91kmbvn">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28" data-id="element-114" data-mp="1b6lkz3wpxbr9">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.45
      }} data-id="element-115" data-mp="1b8euqs2moc3h">
          <h2 className="max-w-4xl font-display text-5xl uppercase leading-[0.92] sm:text-6xl lg:text-7xl" data-id="element-116" data-mp="1p5l8g88hvklu">
            What growth actually looks like.
          </h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/60 sm:text-lg" data-id="element-117" data-mp="3m07wi7qu2svf">
            These are real Stripe numbers from a business we work with. Not a projection, not a best case. This is what compounding looks like when the website, ads, and follow-up all pull in the same direction.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6" data-id="element-118" data-mp="173xl51fuzl80">
          {STATS.map((stat, index) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4,
          delay: index * 0.07
        }} className="border-t border-brand-blue pt-4" data-id="element-119" data-mp="1239uigvjl6eb" data-mpi={index}>
              <div className="font-display text-5xl uppercase leading-none text-brand-blue sm:text-6xl" data-id="element-120" data-mp="1pvqdctn3t2eo">{stat.value}</div>
              <p className="mt-3 max-w-[15rem] font-mono-eyebrow text-[10px] font-semibold uppercase tracking-[0.15em] text-white/55" data-id="element-121" data-mp="1zrtq0tfl0jfv">
                {stat.label}
              </p>
            </motion.div>)}
        </div>

        <motion.button type="button" onClick={() => setActiveScreenshot(FEATURED_SCREENSHOT)} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.45,
        delay: 0.12
      }} className="group relative mx-auto mt-16 block w-full max-w-3xl overflow-hidden rounded-xl border border-white/20 bg-white p-2 text-left shadow-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-4 focus-visible:ring-offset-[#111415] lg:mt-20 lg:p-3" aria-label={`Expand Stripe chart: ${FEATURED_SCREENSHOT.caption}`} data-id="element-122" data-mp="2eg3kgm028hkn">
          <img src={FEATURED_SCREENSHOT.src} alt={FEATURED_SCREENSHOT.alt} className="aspect-[4/3] w-full rounded-lg object-cover object-top transition-transform duration-300 group-hover:scale-[1.01]" data-id="element-123" data-mp="1qpzi0hylynv8" />
          <span className="absolute bottom-6 right-6 flex h-9 w-9 items-center justify-center rounded-full bg-black text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 lg:bottom-7 lg:right-7" data-id="element-124" data-mp="19ntx6sda188b">
            <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-125" data-mp="zwz16wa8au9o" />
          </span>
        </motion.button>
        <p className="mx-auto mt-5 max-w-3xl text-center font-mono-eyebrow text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40" data-id="element-126" data-mp="uy5xfvkquhmp">
          Select the chart to inspect the original Stripe reporting
        </p>
      </div>

      {activeScreenshot && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={`Expanded Stripe screenshot: ${activeScreenshot.caption}`} onClick={() => setActiveScreenshot(null)} data-id="element-127" data-mp="gywkr5vqy5nh">
          <div className="relative max-h-full w-full max-w-6xl" onClick={event => event.stopPropagation()} data-id="element-128" data-mp="26duhof6m52jt">
            <img src={activeScreenshot.src} alt={activeScreenshot.alt} className="max-h-[82vh] w-full rounded-sm object-contain" data-id="element-129" data-mp="2euboy7qhlzv5" />
            <div className="mt-3 flex items-center justify-between gap-4 text-white" data-id="element-130" data-mp="2iulujjadao9h">
              <span className="font-mono-eyebrow text-xs font-semibold uppercase tracking-[0.15em]" data-id="element-131" data-mp="2no6v9j3chwcd">{activeScreenshot.caption}</span>
              <button type="button" onClick={() => setActiveScreenshot(null)} className="flex h-10 w-10 items-center justify-center border border-white/25 text-white transition-colors hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" aria-label="Close expanded screenshot" data-id="element-132" data-mp="1vw8v9fmzver0">
                <XIcon className="h-5 w-5" data-id="element-133" data-mp="3gccg05ueflc7" />
              </button>
            </div>
          </div>
        </div>}
    </section>;
}